import { createAsyncThunk } from "@reduxjs/toolkit";
import API from "src/API";
import { EQAuthParamType } from "src/Types";
import { ReturnStateType } from "src/Types/ReDoc/Return";
import { Select2WebElementType } from "src/Types/ReDoc";
import { PAUSE_START_KEY } from "./ReturnSlicer";

/** Обновление состояния виджета re:turn из ответа backend */
const dispatchReturnState = (thunkAPI: { dispatch: (arg: unknown) => unknown }, state: ReturnStateType) => {
	thunkAPI.dispatch({ type: "eQueueReturn/updateState", payload: state });
};

/** Обновление счетчиков/очереди после успешного действия */
const refreshReturnQueue = (thunkAPI: { dispatch: (arg: unknown) => unknown }, withQueueList: boolean = false) => {
	thunkAPI.dispatch(returnGetQueueState());
	if (withQueueList) {
		thunkAPI.dispatch(returnGetQueue());
	}
};

/** получить настройки электронной очереди */
export const returnGetConfig = createAsyncThunk("eQueueReturn/GetConfig", async (_, thunkAPI) => {
	const result = await API.ReturnEQ.getConfig();
	if (!result.hasError) {
		thunkAPI.dispatch({ type: "eQueueReturn/setConfig", payload: result.result });
	}
	return result;
});

/** сохранить настройки электронной очереди */
export const returnSaveConfig = createAsyncThunk("eQueueReturn/SaveConfig", async (payload: EQAuthParamType) => {
	return await API.ReturnEQ.setConfig(payload);
});

/** запустить электронную очередь */
export const returnStart = createAsyncThunk("eQueueReturn/Start", async (payload: string, thunkAPI) => {
	const data = await API.ReturnEQ.start(payload);
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** получить текущий статус ЭО */
export const returnGetStatus = createAsyncThunk("eQueueReturn/GetStatus", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.getStatus();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** получить счетчики очереди */
export const returnGetQueueState = createAsyncThunk(
	"eQueueReturn/GetQueueState",
	async () => await API.ReturnEQ.getQueueState()
);

/** получить список талонов очереди */
export const returnGetQueue = createAsyncThunk(
	"eQueueReturn/GetQueue",
	async (term?: string) => await API.ReturnEQ.getQueue(term || "")
);

/** Вызов следующего посетителя */
export const returnCall = createAsyncThunk("eQueueReturn/Call", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.call();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Получение окон для перенаправления */
export const returnGetWindows = createAsyncThunk(
	"eQueueReturn/GetWindows",
	async (): Promise<Select2WebElementType[]> => await API.ReturnEQ.getWindows()
);

/** Вызов посетителя по идентификатору талона */
export const returnCallById = createAsyncThunk("eQueueReturn/CallById", async (payload: string, thunkAPI) => {
	const data = await API.ReturnEQ.callById(payload);
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Начало обслуживания текущего посетителя */
export const returnBeginServing = createAsyncThunk("eQueueReturn/BeginServing", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.beginServing();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** Перенаправление посетителя в другое окно */
export const returnTransfer = createAsyncThunk("eQueueReturn/Transfer", async (payload: string, thunkAPI) => {
	const data = await API.ReturnEQ.transfer(payload);
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI, true);
	}
	return data;
});

/** Неявка посетителя */
export const returnReject = createAsyncThunk("eQueueReturn/Reject", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.reject();
	if (!data.hasError) {
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** Уйти на перерыв */
export const returnBeginPause = createAsyncThunk("eQueueReturn/BeginPause", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.beginBreak();
	if (!data.hasError) {
		localStorage.setItem(PAUSE_START_KEY, new Date().toISOString());
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
	}
	return data;
});

/** Завершить перерыв */
export const returnEndPause = createAsyncThunk("eQueueReturn/EndPause", async (_, thunkAPI) => {
	const data = await API.ReturnEQ.endBreak();
	if (!data.hasError) {
		localStorage.removeItem(PAUSE_START_KEY);
		dispatchReturnState(thunkAPI, data.result as ReturnStateType);
		refreshReturnQueue(thunkAPI);
	}
	return data;
});

/** Завершение работы с сервером ЭО */
export const returnExit = createAsyncThunk("eQueueReturn/OnExit", async (_payload: string, thunkAPI) => {
	const data = await API.ReturnEQ.finish();
	if (!data.hasError) {
		localStorage.removeItem(PAUSE_START_KEY);
		thunkAPI.dispatch({ type: "eQueueReturn/exit", payload: data.result });
	}
	return data;
});
